// The delivery tracker: two legs as tabs, each a paged, sortable table of that leg's rows.
//
// The active tab is the URL's `:leg`, and the sort, filter and page are its query string - there
// is no component state for any of them, so a reload lands where the user was and a view is a
// link. The column definitions come from the API, and everything below the tabs waits for them:
// the URL cannot be read until it is known which columns sort. The filters are a set of values
// per stored column (TX-US-0020), shown as chips above whichever presentation is on screen.
//
// Each leg keeps its view while it is not shown (legViews.js): the tab link carries the query
// string that leg was last seen with, so switching away and back returns to the same filters,
// sort and page.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useParams, useSearchParams } from 'react-router';

import { downloadTrackerExport, getDeliveryTotals, listDeliveries } from '../api.js';
import { useMe } from '../MeContext.js';
import { WRITER_ROLES } from '../roles.js';
import Icon from '../ui/Icon.jsx';
import { notify } from '../ui/notify.jsx';
import Pager from '../ui/Pager.jsx';
import usePageTrail from '../ui/usePageTrail.js';
import AttachmentsDialog from './attachments/AttachmentsDialog.jsx';
import DeleteDialog from './DeleteDialog.jsx';
import DeliveryTable from './DeliveryTable.jsx';
import DeliveryTiles from './DeliveryTiles.jsx';
import FilterChips from './FilterChips.jsx';
import { recallView, rememberView } from './legViews.js';
import { trackerUrl } from './trackerUrl.js';
import useColumns from './useColumns.js';
import useTableFits from './useTableFits.js';

const PAGE_SIZE = 25;

export default function DeliveryTracker() {
  const { leg } = useParams();
  const { search } = useLocation();
  const { legs, error } = useColumns();
  useEffect(() => rememberView(leg, search), [leg, search]);
  const me = useMe();
  // Offering only what the API would accept (AC7); the API refuses a write without OPERATIONS regardless.
  const canWrite = Boolean(me?.roles?.some((role) => WRITER_ROLES.includes(role)));
  const [exporting, setExporting] = useState(false);

  // The whole tracker as one workbook (TX-US-0015): everyone who can read it can export it. The
  // filename is the API's; the button is held while the file is being made.
  const exportWorkbook = async () => {
    setExporting(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      await downloadTrackerExport(`delivery-tracker-${day}.xlsx`);
    } catch (err) {
      notify(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="register tracker">
      <header className="register-header">
        <h2>Delivery tracker</h2>
        <div className="register-actions">
          <button type="button" className="secondary" onClick={exportWorkbook} disabled={exporting}>
            <Icon name="download" /> {exporting ? 'Exporting…' : 'Export'}
          </button>
          {canWrite && legs?.[leg] && (
            <Link className="icon-button primary" to={`/deliveries/${leg}/new`}
                  aria-label="Add row" title="Add row">
              <Icon name="plus" />
            </Link>
          )}
        </div>
      </header>

      {legs && (
        <nav className="tabs" aria-label="Leg">
          {Object.keys(legs).map((name) => (
            // A tab is a place, and the link to it is the view that leg was last on. The sort,
            // filter and cursor belong to the leg they were issued for, so they are never
            // carried across; a leg not yet visited opens on its default view.
            <NavLink key={name} to={`/deliveries/${name}${name === leg ? search : recallView(name)}`}
                     end>
              {legLabel(name)}
            </NavLink>
          ))}
        </nav>
      )}

      {error && <p className="error">{error}</p>}
      {!legs && !error && <p className="muted">Loading…</p>}

      {legs && !legs[leg] && <p className="error">There is no {leg} tracker.</p>}

      {/* Keyed on the leg so that switching tabs remounts: the page, the error and the paging
          trail all belong to one leg, and a remount discards them without a reset in each. */}
      {legs?.[leg] && (
        <LegTable key={leg} leg={leg} columns={legs[leg]} canWrite={canWrite} />
      )}
    </section>
  );
}

function LegTable({ leg, columns, canWrite }) {
  const url = useMemo(() => trackerUrl(columns), [columns]);
  const [searchParams, setSearchParams] = useSearchParams();
  const query = url.fromSearchParams(searchParams);

  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);
  // Bumped after a delete: the URL has not changed but the page it names has.
  const [generation, setGeneration] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [totals, setTotals] = useState(null);
  // Tiles wherever the table would need a horizontal scrollbar; measured, because the width a
  // leg's table needs is a property of the columns the API served and the values on this page -
  // and of the sums under them, which arrive separately.
  const laidOut = useMemo(() => ({ page, totals }), [page, totals]);
  const { tiles, sectionRef, scrollRef } = useTableFits(laidOut);

  const apply = useCallback(
    (next) => setSearchParams(new URLSearchParams(url.toQueryString(next))),
    [setSearchParams, url],
  );

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);

    listDeliveries(leg, url.toApiSearch(url.fromSearchParams(searchParams), PAGE_SIZE))
      .then((result) => {
        if (!cancelled) setPage(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
    // The URL is the dependency. Anything else here would be a second source of truth - except
    // the generation, which says the same URL must be read again.
  }, [leg, url, searchParams, generation]);

  // The total row (TX-US-0017) is of the whole view: read with the filters and never with the
  // page. A delete changes it, so the generation is a dependency too.
  const filterSearch = url.filterSearch(query);
  useEffect(() => {
    let cancelled = false;
    setTotals(null);
    getDeliveryTotals(leg, filterSearch)
      .then((result) => {
        if (!cancelled) setTotals(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [leg, filterSearch, generation]);

  const paging = usePageTrail({ query, page, apply, withCursor: url.withCursor });

  const onSort = (key) => {
    paging.reset();
    apply(url.nextSort(query, key));
  };

  const onFilter = (key, values) => {
    paging.reset();
    apply(url.withValues(query, key, values));
  };

  const onClearFilters = () => {
    paging.reset();
    apply(url.clearFilters(query));
  };

  // Confirmed in the dialog, confirmed again in the toast (AC10): the dialog closes on success,
  // and the row's absence from the table is not on its own a statement that it was deleted.
  const onDeleted = (deliveryId) => {
    setDeleting(null);
    notify(`${deliveryId} deleted.`);
    setGeneration((current) => current + 1);
  };

  return (
    <div className="leg" ref={sectionRef}>
      {Object.keys(query.filters).length > 0 && (
        <div className="filters">
          <FilterChips columns={columns} query={query} onFilter={onFilter}
                       onClear={onClearFilters} />
        </div>
      )}

      {error && <p className="error">{error}</p>}

      {/* One presentation in the DOM at a time, as the register: both rendered with one hidden
          would put every row into the accessibility tree twice. */}
      {page && (tiles
        ? (
          <DeliveryTiles leg={leg} columns={columns} rows={page.deliveries} totals={totals}
                         query={query} url={url} onSort={onSort} onFilter={onFilter}
                         canWrite={canWrite} onDelete={setDeleting} onAttachments={setViewing} />
        )
        : (
          <DeliveryTable leg={leg} columns={columns} rows={page.deliveries} totals={totals}
                         query={query} url={url} onSort={onSort} onFilter={onFilter}
                         canWrite={canWrite} onDelete={setDeleting} onAttachments={setViewing}
                         scrollRef={scrollRef} />
        ))}

      {deleting && (
        <DeleteDialog leg={leg} deliveryId={deleting} onClose={() => setDeleting(null)}
                      onDeleted={onDeleted} />
      )}

      {viewing && (
        <AttachmentsDialog leg={leg} row={viewing} onClose={() => setViewing(null)}
                           // The count on the row is the server's; a delete in the viewer
                           // changes it, so the page is read again.
                           onChanged={() => setGeneration((current) => current + 1)} />
      )}

      {busy && <p className="muted">Loading…</p>}

      {!busy && page?.deliveries.length === 0 && (
        <p className="empty">
          No rows here.
          {canWrite && <> <Link to={`/deliveries/${leg}/new`}>Add the first one</Link>.</>}
        </p>
      )}

      <Pager
        busy={busy}
        hasPrevious={paging.hasPrevious}
        hasMore={Boolean(page?.has_more)}
        onPrevious={paging.onPrevious}
        onNext={paging.onNext}
      />
    </div>
  );
}

// The legs are served as their identifiers; the tab shows the word with its initial raised.
const legLabel = (name) => name.charAt(0).toUpperCase() + name.slice(1);
